// frontend/src/components/dfs/MatchCard.tsx
import React from 'react';
import { ApiMatchInfo } from '@/api/dfs';
import { getAbbreviation } from '@/utils/teams';
import { format, parseISO } from 'date-fns';

interface MatchCardProps {
  match: ApiMatchInfo;
  onClick: () => void;
  isSelected?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({ match, onClick, isSelected }) => {
  const homeAbbr = getAbbreviation(match.match_home_team_name);
  const awayAbbr = getAbbreviation(match.match_away_team_name);
  const hasScore = match.home_score != null && match.away_score != null;
  const isLive = hasScore && match.match_clock &&
    !['FT', 'AET', 'PEN'].includes(match.match_clock.toUpperCase());
  const isFinished = hasScore && match.match_clock &&
    ['FT', 'AET', 'PEN'].includes(match.match_clock.toUpperCase());

  const showStatusBadge = match.match_status_code &&
    ['PPD', 'SUSP', 'CANC'].includes(match.match_status_code.toUpperCase());

  const kickoffLabel = match.match_date
    ? format(parseISO(match.match_date), 'EEE h:mm a')
    : null;

  const isPreGame = !hasScore && !showStatusBadge;

  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 flex flex-col items-center px-3 py-1.5 rounded border cursor-pointer transition-all duration-150
        ${isSelected
          ? 'border-accent-yellow bg-accent-yellow/10 brightness-110'
          : isPreGame
            ? 'border-accent-yellow/40 bg-primary-dark hover:brightness-125 active:scale-95'
            : 'border-gray-700/60 bg-primary-dark hover:brightness-125 active:scale-95'
        }`}
      aria-label={`View players for match: ${match.match_home_team_name} vs ${match.match_away_team_name}`}
    >
      {/* Row 1: Teams & Score */}
      <div className="flex items-center gap-1.5 text-caption">
        <span className="font-semibold text-gray-200">{homeAbbr}</span>
        {hasScore ? (
          <span className="font-bold text-white">{match.home_score} - {match.away_score}</span>
        ) : (
          <span className="text-white">-</span>
        )}
        <span className="font-semibold text-gray-200">{awayAbbr}</span>
      </div>

      {/* Row 2: Status / Clock / Kickoff */}
      {showStatusBadge ? (
        <span className="text-micro font-bold mt-0.5 px-1 py-0.5 rounded-sm bg-yellow-600 text-yellow-100">
          {match.match_status_code}
        </span>
      ) : isLive ? (
        <span className="text-green-400 font-semibold text-badge flex items-center mt-0.5">
          <span className="w-1 h-1 bg-green-500 rounded-full animate-pulse mr-1" />
          {match.match_clock}
        </span>
      ) : isFinished ? (
        <span className="text-gray-500 font-semibold text-badge mt-0.5">{match.match_clock}</span>
      ) : kickoffLabel ? (
        <span className="text-accent-yellow text-badge mt-0.5">{kickoffLabel}</span>
      ) : null}
    </button>
  );
};

export default MatchCard;
